import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { hash2, noise2, smoothstep } from '../core/noise';
import {
  RUNTIME_MODEL_CATALOG,
  RUNTIME_MODEL_PLACEMENT_SEEDS,
  bakeEnvironmentModelGeometry,
  type RuntimeModelDefinition,
} from '../models/runtimeModels';
import { biomeWeightsAt } from './biomes';
import { START_X, START_Z, terrainColor, terrainHeight } from './terrain';

const CHUNK_SIZE = 104;
const CHUNK_SEGMENTS = 32;
const VIEW_RADIUS = 4;
const IMMEDIATE_VIEW_RADIUS = 1;
const CHUNKS_PER_UPDATE = 1;
const COLLISION_CELL_SIZE = 8;
const PINE_LAYER_COUNTS = [3, 4, 5] as const;

type PineLayerCount = (typeof PINE_LAYER_COUNTS)[number];

export interface Obstacle {
  readonly x: number;
  readonly z: number;
  readonly radius: number;
  readonly kind: 'tree' | 'rock' | 'ice';
}

interface TerrainChunk {
  readonly x: number;
  readonly z: number;
  readonly anchorHeight: number;
  readonly group: THREE.Group;
  readonly terrainGeometry: THREE.BufferGeometry;
  readonly obstacles: Obstacle[];
}

interface ChunkCoordinate {
  readonly x: number;
  readonly z: number;
}

interface TreePlacement {
  x: number;
  y: number;
  z: number;
  scale: number;
  width: number;
  pineLayers: PineLayerCount;
  layerRotations: readonly number[];
  rotation: number;
  tint: THREE.Color;
}

interface ObjectPlacement {
  x: number;
  y: number;
  z: number;
  scaleX: number;
  scaleY: number;
  scaleZ: number;
  rotation: number;
  tiltX?: number;
  tiltZ?: number;
  tint: THREE.Color;
}

interface RuntimeModelPlacement {
  x: number;
  y: number;
  z: number;
  scale: number;
  rotation: number;
}

function createRockGeometry(seed: number): THREE.BufferGeometry {
  const geometry = new THREE.DodecahedronGeometry(1, 0);
  const positions = geometry.getAttribute('position') as THREE.BufferAttribute;

  // Distort shared coordinates by position, rather than by face, so adjacent
  // facets stay welded while each rock gets a less manufactured silhouette.
  for (let index = 0; index < positions.count; index += 1) {
    const x = positions.getX(index);
    const y = positions.getY(index);
    const z = positions.getZ(index);
    const horizontal = 0.88 + hash2(Math.round(x * 101), Math.round(z * 103), seed) * 0.2;
    const vertical = 0.9 + hash2(Math.round(y * 107), Math.round(x * 109), seed + 1) * 0.16;
    positions.setXYZ(index, x * horizontal, y * vertical, z * horizontal);
  }

  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

interface IceShardShape {
  readonly x: number;
  readonly z: number;
  readonly height: number;
  readonly bottomRadius: number;
  readonly topRadius: number;
  readonly leanX: number;
  readonly leanZ: number;
  readonly width: number;
  readonly depth: number;
  readonly rotation: number;
}

function createPointedIceShard(shape: IceShardShape): THREE.BufferGeometry {
  const sides = 5;
  const positions: number[] = [];
  const bottom: THREE.Vector3[] = [];
  const shoulder: THREE.Vector3[] = [];
  const cosine = Math.cos(shape.rotation);
  const sine = Math.sin(shape.rotation);

  const transform = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(
    shape.x + x * cosine + z * sine,
    y,
    shape.z - x * sine + z * cosine,
  );

  for (let index = 0; index < sides; index += 1) {
    const angle = (index / sides) * Math.PI * 2 + Math.PI / 10;
    bottom.push(transform(
      Math.cos(angle) * shape.bottomRadius * shape.width,
      0,
      Math.sin(angle) * shape.bottomRadius * shape.depth,
    ));
    shoulder.push(transform(
      Math.cos(angle) * shape.topRadius * shape.width + shape.leanX * 0.74,
      shape.height * 0.74,
      Math.sin(angle) * shape.topRadius * shape.depth + shape.leanZ * 0.74,
    ));
  }

  const apex = transform(
    shape.leanX + shape.topRadius * shape.width * 0.38,
    shape.height,
    shape.leanZ - shape.topRadius * shape.depth * 0.24,
  );
  const appendTriangle = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3): void => {
    positions.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
  };

  for (let index = 0; index < sides; index += 1) {
    const next = (index + 1) % sides;
    appendTriangle(bottom[index]!, bottom[next]!, shoulder[next]!);
    appendTriangle(bottom[index]!, shoulder[next]!, shoulder[index]!);
    appendTriangle(shoulder[index]!, shoulder[next]!, apex);
  }
  for (let index = 1; index < sides - 1; index += 1) {
    appendTriangle(bottom[0]!, bottom[index + 1]!, bottom[index]!);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}

/**
 * Builds one compound slab of wind-compressed ice for ambient Blue Ice fields.
 * The uneven, shared footprint avoids the collectible-gem read of isolated
 * octahedra while the merged result remains one instanced draw batch.
 */
export function createIcePressureRidgeGeometry(): THREE.BufferGeometry {
  const shardShapes: readonly IceShardShape[] = [
    {
      x: -0.1, z: -0.02, height: 1.5, bottomRadius: 0.34, topRadius: 0.25,
      leanX: 0.26, leanZ: -0.1, width: 0.94, depth: 0.62, rotation: 0.08,
    },
    {
      x: -0.76, z: 0.1, height: 1.06, bottomRadius: 0.27, topRadius: 0.19,
      leanX: -0.25, leanZ: 0.11, width: 0.92, depth: 0.58, rotation: -0.22,
    },
    {
      x: 0.66, z: -0.26, height: 1.18, bottomRadius: 0.29, topRadius: 0.2,
      leanX: 0.25, leanZ: 0.14, width: 1.02, depth: 0.64, rotation: 0.28,
    },
    {
      x: 0.28, z: 0.53, height: 0.78, bottomRadius: 0.22, topRadius: 0.14,
      leanX: -0.12, leanZ: 0.17, width: 0.88, depth: 0.56, rotation: -0.12,
    },
    {
      x: -0.36, z: -0.53, height: 0.54, bottomRadius: 0.25, topRadius: 0.18,
      leanX: -0.1, leanZ: -0.05, width: 1.08, depth: 0.68, rotation: 0.36,
    },
  ];
  const pieces: THREE.BufferGeometry[] = [];

  const skirt = new THREE.CylinderGeometry(0.82, 1, 0.14, 7, 1, false).toNonIndexed();
  skirt.deleteAttribute('uv');
  skirt.scale(1.2, 1, 0.7);
  skirt.translate(0, 0.07, 0);
  pieces.push(skirt);

  for (const shape of shardShapes) {
    pieces.push(createPointedIceShard(shape));
  }

  const geometry = mergeGeometries(pieces, false);
  for (const piece of pieces) piece.dispose();
  geometry.name = 'ambient-ice-pressure-ridge';
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

export function pineLayerShape(layerIndex: number, layerCount: PineLayerCount): { radius: number; y: number } {
  const amount = layerIndex / (layerCount - 1);
  const radiusScale = layerCount >= 4 ? 1.06 : 1;
  return {
    radius: (0.92 - amount * 0.44) * radiusScale,
    y: 1.15 + amount * 1.17 - amount * amount * 0.1,
  };
}

function pineLayerCount(sizeChoice: number, variation: number): PineLayerCount {
  const weightedSize = Math.max(0, Math.min(0.999, sizeChoice + (variation - 0.5) * 0.3));
  return PINE_LAYER_COUNTS[Math.floor(weightedSize * PINE_LAYER_COUNTS.length)]!;
}

function createSnowCapGeometry(source: THREE.BufferGeometry): THREE.BufferGeometry {
  const sourcePositions = source.getAttribute('position') as THREE.BufferAttribute;
  const positions: number[] = [];

  for (let index = 0; index < sourcePositions.count; index += 3) {
    const a = new THREE.Vector3().fromBufferAttribute(sourcePositions, index);
    const b = new THREE.Vector3().fromBufferAttribute(sourcePositions, index + 1);
    const c = new THREE.Vector3().fromBufferAttribute(sourcePositions, index + 2);
    if ((a.y + b.y + c.y) / 3 < 0.31) continue;

    for (const vertex of [a, b, c]) {
      const overhang = 1.035 - Math.max(vertex.y, 0) * 0.015;
      positions.push(vertex.x * overhang, vertex.y + 0.025, vertex.z * overhang);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

export class World {
  private readonly root = new THREE.Group();
  private readonly chunks = new Map<string, TerrainChunk>();
  private readonly collisionBins = new Map<string, Obstacle[]>();
  private readonly nearbyObstacleScratch: Obstacle[] = [];
  private pendingChunks: ChunkCoordinate[] = [];
  private centerChunkX = Number.NaN;
  private centerChunkZ = Number.NaN;
  private collisionSearchRing = 1;
  private readonly terrainMaterial = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.94,
    metalness: 0,
    flatShading: true,
  });

  private readonly trunkGeometry = new THREE.CylinderGeometry(0.14, 0.2, 1.2, 6).translate(0, 0.6, 0);
  private readonly pineLayerGeometry = new THREE.ConeGeometry(1, 1.1, 7);
  private readonly rockGeometries = [createRockGeometry(5101), createRockGeometry(5209)];
  private readonly rockSnowGeometries = this.rockGeometries.map(createSnowCapGeometry);
  private readonly iceGeometry = createIcePressureRidgeGeometry();
  private readonly runtimeModelGeometries = new Map(
    RUNTIME_MODEL_CATALOG.map((definition) => [definition.id, bakeEnvironmentModelGeometry(definition.id)]),
  );

  private readonly trunkMaterial = new THREE.MeshStandardMaterial({
    color: 0x795f52,
    roughness: 1,
    flatShading: true,
  });
  private readonly pineMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    vertexColors: true,
    emissive: 0x225d31,
    emissiveIntensity: 0.3,
    roughness: 0.96,
    flatShading: true,
  });
  private readonly snowCapMaterial = new THREE.MeshStandardMaterial({
    color: 0xeaf3ef,
    roughness: 0.9,
    flatShading: true,
  });
  private readonly rockMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    vertexColors: true,
    emissive: 0x465052,
    emissiveIntensity: 0.34,
    roughness: 1,
    flatShading: true,
  });
  private readonly iceMaterial = new THREE.MeshStandardMaterial({
    color: 0xa6e8f4,
    vertexColors: true,
    emissive: 0x3aa7c5,
    emissiveIntensity: 0.48,
    roughness: 0.42,
    metalness: 0.05,
    flatShading: true,
  });
  private readonly runtimeModelMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    vertexColors: true,
    roughness: 0.9,
    metalness: 0,
    flatShading: true,
  });

  constructor(private readonly scene: THREE.Scene) {
    this.root.name = 'floating-mountain-world';
    this.scene.add(this.root);
  }

  update(originX: number, originZ: number, originHeight: number): void {
    const centerX = Math.floor(originX / CHUNK_SIZE);
    const centerZ = Math.floor(originZ / CHUNK_SIZE);
    if (centerX !== this.centerChunkX || centerZ !== this.centerChunkZ) {
      this.reconcileChunks(centerX, centerZ);
      this.centerChunkX = centerX;
      this.centerChunkZ = centerZ;
    }

    // The inner 3x3 is created synchronously by reconcileChunks so collision
    // queries are always complete. Far, fog-softened rings arrive one at
    // a time while the canvas performs its wake-up fade, avoiding one large
    // startup or boundary-crossing stall.
    this.populatePendingChunks(CHUNKS_PER_UPDATE);

    this.root.position.set(-originX, -originHeight, -originZ);
  }

  obstaclesNear(x: number, z: number): readonly Obstacle[] {
    const cellX = Math.floor(x / COLLISION_CELL_SIZE);
    const cellZ = Math.floor(z / COLLISION_CELL_SIZE);
    const result = this.nearbyObstacleScratch;
    result.length = 0;

    for (let dz = -this.collisionSearchRing; dz <= this.collisionSearchRing; dz += 1) {
      for (let dx = -this.collisionSearchRing; dx <= this.collisionSearchRing; dx += 1) {
        const bin = this.collisionBins.get(this.collisionBinKey(cellX + dx, cellZ + dz));
        if (bin) result.push(...bin);
      }
    }

    return result;
  }

  dispose(): void {
    for (const chunk of this.chunks.values()) {
      this.root.remove(chunk.group);
      this.disposeChunk(chunk);
    }
    this.chunks.clear();
    this.collisionBins.clear();
    this.pendingChunks = [];
    this.nearbyObstacleScratch.length = 0;
    this.scene.remove(this.root);
    this.root.clear();

    this.terrainMaterial.dispose();
    this.trunkGeometry.dispose();
    this.pineLayerGeometry.dispose();
    for (const geometry of this.rockGeometries) geometry.dispose();
    for (const geometry of this.rockSnowGeometries) geometry.dispose();
    this.iceGeometry.dispose();
    for (const geometry of this.runtimeModelGeometries.values()) geometry.dispose();
    this.trunkMaterial.dispose();
    this.pineMaterial.dispose();
    this.snowCapMaterial.dispose();
    this.rockMaterial.dispose();
    this.iceMaterial.dispose();
    this.runtimeModelMaterial.dispose();
  }

  private reconcileChunks(centerX: number, centerZ: number): void {
    for (const [key, chunk] of this.chunks) {
      if (
        Math.abs(chunk.x - centerX) <= VIEW_RADIUS
        && Math.abs(chunk.z - centerZ) <= VIEW_RADIUS
      ) continue;
      this.removeChunk(chunk);
      this.chunks.delete(key);
    }

    // The nearby 3x3 is collision-complete before simulation resumes. Ring
    // order also fills the most visually useful scenery first.
    for (let ring = 0; ring <= IMMEDIATE_VIEW_RADIUS; ring += 1) {
      for (let dz = -ring; dz <= ring; dz += 1) {
        for (let dx = -ring; dx <= ring; dx += 1) {
          if (Math.max(Math.abs(dx), Math.abs(dz)) !== ring) continue;
          this.ensureChunk(centerX + dx, centerZ + dz);
        }
      }
    }

    const pending: ChunkCoordinate[] = [];
    for (let ring = IMMEDIATE_VIEW_RADIUS + 1; ring <= VIEW_RADIUS; ring += 1) {
      for (let dz = -ring; dz <= ring; dz += 1) {
        for (let dx = -ring; dx <= ring; dx += 1) {
          if (Math.max(Math.abs(dx), Math.abs(dz)) !== ring) continue;
          const x = centerX + dx;
          const z = centerZ + dz;
          if (!this.chunks.has(this.chunkKey(x, z))) pending.push({ x, z });
        }
      }
    }
    this.pendingChunks = pending;
  }

  private populatePendingChunks(limit: number): void {
    for (let count = 0; count < limit; count += 1) {
      const coordinate = this.pendingChunks.shift();
      if (!coordinate) return;
      this.ensureChunk(coordinate.x, coordinate.z);
    }
  }

  private ensureChunk(x: number, z: number): void {
    const key = this.chunkKey(x, z);
    if (this.chunks.has(key)) return;
    const chunk = this.createChunk(x, z);
    chunk.group.position.set(x * CHUNK_SIZE, chunk.anchorHeight, z * CHUNK_SIZE);
    this.chunks.set(key, chunk);
    this.addChunkCollisions(chunk);
    this.root.add(chunk.group);
  }

  private removeChunk(chunk: TerrainChunk): void {
    this.root.remove(chunk.group);
    this.removeChunkCollisions(chunk);
    this.disposeChunk(chunk);
  }

  private addChunkCollisions(chunk: TerrainChunk): void {
    for (const obstacle of chunk.obstacles) {
      const key = this.collisionBinKey(
        Math.floor(obstacle.x / COLLISION_CELL_SIZE),
        Math.floor(obstacle.z / COLLISION_CELL_SIZE),
      );
      const bin = this.collisionBins.get(key);
      if (bin) bin.push(obstacle);
      else this.collisionBins.set(key, [obstacle]);
      this.collisionSearchRing = Math.max(
        this.collisionSearchRing,
        Math.ceil((obstacle.radius + 0.5) / COLLISION_CELL_SIZE),
      );
    }
  }

  private removeChunkCollisions(chunk: TerrainChunk): void {
    for (const obstacle of chunk.obstacles) {
      const key = this.collisionBinKey(
        Math.floor(obstacle.x / COLLISION_CELL_SIZE),
        Math.floor(obstacle.z / COLLISION_CELL_SIZE),
      );
      const bin = this.collisionBins.get(key);
      if (!bin) continue;
      const index = bin.indexOf(obstacle);
      if (index >= 0) bin.splice(index, 1);
      if (bin.length === 0) this.collisionBins.delete(key);
    }
  }

  private createChunk(chunkX: number, chunkZ: number): TerrainChunk {
    const startX = chunkX * CHUNK_SIZE;
    const startZ = chunkZ * CHUNK_SIZE;
    const anchorHeight = terrainHeight(startX + CHUNK_SIZE / 2, startZ + CHUNK_SIZE / 2);
    const group = new THREE.Group();
    const terrainGeometry = this.createTerrainGeometry(startX, startZ, anchorHeight);
    const terrain = new THREE.Mesh(terrainGeometry, this.terrainMaterial);
    terrain.frustumCulled = true;
    terrain.receiveShadow = true;
    group.add(terrain);

    const obstacles: Obstacle[] = [];
    const trees: TreePlacement[] = [];
    const rocks: [ObjectPlacement[], ObjectPlacement[]] = [[], []];
    const rockCaps: [ObjectPlacement[], ObjectPlacement[]] = [[], []];
    const ice: ObjectPlacement[] = [];

    for (let index = 0; index < 350; index += 1) {
      const worldX = startX + hash2(chunkX * 197 + index, chunkZ * 269, 3109) * CHUNK_SIZE;
      const worldZ = startZ + hash2(chunkX * 281 + index, chunkZ * 181, 3203) * CHUNK_SIZE;
      const spawnDx = worldX - START_X;
      const spawnDz = worldZ - START_Z;
      // Give the opening vista enough room to read and the player a fair first
      // turn before the forest closes in around the run.
      if (spawnDx * spawnDx + spawnDz * spawnDz < 900) continue;

      const weights = biomeWeightsAt(worldX, worldZ);
      const openRibbon = smoothstep(0.045, 0.2, Math.abs(noise2(worldX / 185, worldZ / 300, 3301)));
      const grove = smoothstep(-0.55, 0.42, noise2(worldX / 92, worldZ / 108, 3323));
      const density = (
        0.012
        + weights.lanternwood * 0.6
        + weights.timberline * 0.045
      ) * (1 - smoothstep(0.48, 0.82, weights.blueIce))
        * (0.14 + openRibbon * 0.86) * (0.58 + grove * 0.58);
      const choice = hash2(chunkX * 383 + index, chunkZ * 419, 3407);
      if (choice > density) continue;

      const height = terrainHeight(worldX, worldZ, weights);
      const sizeChoice = hash2(chunkX * 443 + index, chunkZ * 461, 3511);
      // Skip the undersized trees that read as pointy stumps poking through
      // the snow rather than as part of the surrounding forest.
      if (sizeChoice < 0.16) continue;
      const scale = 0.82 + Math.pow(sizeChoice, 1.45) * 1.85;
      const width = 0.78 + hash2(chunkX * 457 + index, chunkZ * 479, 3541) * 0.48;
      const cool = hash2(chunkX * 487 + index, chunkZ * 499, 3607);
      const pineLayers = pineLayerCount(sizeChoice, hash2(chunkX * 467 + index, chunkZ * 481, 3559));
      trees.push({
        x: worldX - startX,
        // Sink the trunk below the sampled surface so even large trees on a
        // steep triangle cannot expose their base or appear to float.
        y: height - anchorHeight - scale * 0.28,
        z: worldZ - startZ,
        scale,
        width,
        pineLayers,
        layerRotations: Array.from(
          { length: pineLayers },
          (_, layerIndex) => hash2(chunkX * 491 + index, chunkZ * 503 + layerIndex, 3593) * Math.PI * 2,
        ),
        rotation: hash2(chunkX * 491 + index, chunkZ * 503, 3593) * Math.PI * 2,
        tint: new THREE.Color().setRGB(0.3 + cool * 0.07, 0.68 + cool * 0.09, 0.35 + cool * 0.06),
      });
      obstacles.push({ x: worldX, z: worldZ, radius: 0.34 + scale * 0.12, kind: 'tree' });
    }

    for (let index = 0; index < 46; index += 1) {
      const worldX = startX + hash2(chunkX * 521 + index, chunkZ * 547, 3701) * CHUNK_SIZE;
      const worldZ = startZ + hash2(chunkX * 563 + index, chunkZ * 587, 3803) * CHUNK_SIZE;
      const weights = biomeWeightsAt(worldX, worldZ);
      const choice = hash2(chunkX * 601 + index, chunkZ * 607, 3907);
      if (choice > 0.055 + weights.timberline * 0.62 + weights.blueIce * 0.12) continue;

      const height = terrainHeight(worldX, worldZ, weights);
      const scale = 0.35 + hash2(chunkX * 613 + index, chunkZ * 631, 4001) * 1.05;
      const scaleY = scale * (0.55 + choice * 0.35);
      const rock: ObjectPlacement = {
        x: worldX - startX,
        y: height - anchorHeight + scaleY * 0.66,
        z: worldZ - startZ,
        scaleX: scale,
        scaleY,
        scaleZ: scale * (0.75 + choice * 0.4),
        rotation: choice * Math.PI * 2,
        tint: new THREE.Color().setRGB(0.62 + choice * 0.1, 0.66 + choice * 0.09, 0.67 + choice * 0.08),
      };
      const shape = index % 2 as 0 | 1;
      rocks[shape].push(rock);
      if (scale > 0.58) {
        rockCaps[shape].push({
          x: rock.x,
          y: rock.y,
          z: rock.z,
          scaleX: rock.scaleX,
          scaleY: rock.scaleY,
          scaleZ: rock.scaleZ,
          rotation: rock.rotation,
          tint: new THREE.Color(0xeaf3f3),
        });
      }
      obstacles.push({ x: worldX, z: worldZ, radius: scale * 0.62, kind: 'rock' });
    }

    for (let index = 0; index < 38; index += 1) {
      const worldX = startX + hash2(chunkX * 641 + index, chunkZ * 653, 4099) * CHUNK_SIZE;
      const worldZ = startZ + hash2(chunkX * 659 + index, chunkZ * 673, 4201) * CHUNK_SIZE;
      const weights = biomeWeightsAt(worldX, worldZ);
      const presence = hash2(chunkX * 677 + index, chunkZ * 683, 4297);
      if (presence > weights.blueIce * 0.34) continue;

      const height = terrainHeight(worldX, worldZ, weights);
      const scaleChoice = hash2(chunkX * 691 + index, chunkZ * 701, 4319);
      const scale = 0.68 + scaleChoice * 0.5;
      const width = 0.9 + hash2(chunkX * 709 + index, chunkZ * 719, 4337) * 0.2;
      const depth = 0.88 + hash2(chunkX * 727 + index, chunkZ * 733, 4357) * 0.24;
      const rotation = hash2(chunkX * 739 + index, chunkZ * 743, 4373) * Math.PI * 2;
      const tint = hash2(chunkX * 751 + index, chunkZ * 757, 4391);
      ice.push({
        x: worldX - startX,
        y: height - anchorHeight - scale * 0.1,
        z: worldZ - startZ,
        scaleX: scale * width,
        scaleY: scale * 1.34,
        scaleZ: scale * depth,
        rotation,
        tiltX: (hash2(chunkX * 761 + index, chunkZ * 769, 4409) - 0.5) * 0.05,
        tiltZ: (hash2(chunkX * 773 + index, chunkZ * 787, 4421) - 0.5) * 0.05,
        tint: new THREE.Color().setRGB(0.5 + tint * 0.1, 0.8 + tint * 0.08, 0.91 + tint * 0.06),
      });
      obstacles.push({ x: worldX, z: worldZ, radius: scale * 0.78, kind: 'ice' });
    }

    this.addTrees(group, trees);
    for (const shape of [0, 1] as const) {
      this.addObjects(group, rocks[shape], this.rockGeometries[shape]!, this.rockMaterial, true);
      this.addObjects(group, rockCaps[shape], this.rockSnowGeometries[shape]!, this.snowCapMaterial);
    }
    this.addObjects(group, ice, this.iceGeometry, this.iceMaterial);
    this.addRuntimeModels(group, startX, startZ, anchorHeight, chunkX, chunkZ, obstacles);

    return { x: chunkX, z: chunkZ, anchorHeight, group, terrainGeometry, obstacles };
  }

  private addRuntimeModels(
    group: THREE.Group,
    startX: number,
    startZ: number,
    anchorHeight: number,
    chunkX: number,
    chunkZ: number,
    obstacles: Obstacle[],
  ): void {
    for (const sourceDefinition of RUNTIME_MODEL_CATALOG) {
      const definition: RuntimeModelDefinition = sourceDefinition;
      const placements: RuntimeModelPlacement[] = [];
      const attempts = definition.landmark ? 24 : 68;
      const seed = RUNTIME_MODEL_PLACEMENT_SEEDS[definition.id];

      for (let index = 0; index < attempts; index += 1) {
        // Every model owns a separate deterministic stream. Adding a future
        // model therefore cannot reshuffle any existing landmark population.
        const worldX = startX + hash2(chunkX * 719 + index, chunkZ * 727, seed) * CHUNK_SIZE;
        const worldZ = startZ + hash2(chunkX * 733 + index, chunkZ * 739, seed + 17) * CHUNK_SIZE;
        const spawnDx = worldX - START_X;
        const spawnDz = worldZ - START_Z;
        const spawnClearance = definition.landmark ? 1_050 : 625;
        if (spawnDx * spawnDx + spawnDz * spawnDz < spawnClearance) continue;

        const choice = hash2(chunkX * 743 + index, chunkZ * 751, seed + 31);
        // biomeWeight and visibility are both capped at one, so candidates
        // above the base density can never pass. Rejecting them here avoids a
        // 25-source biome blend and noise sample for almost every attempt
        // without changing a single accepted placement.
        if (choice > definition.density) continue;
        const weights = biomeWeightsAt(worldX, worldZ);
        const biomeWeight = weights[definition.biome];
        const ribbon = smoothstep(0.045, 0.2, Math.abs(noise2(worldX / 185, worldZ / 300, 3301)));
        const visibility = definition.landmark ? 0.6 + (1 - ribbon) * 0.4 : 0.7 + ribbon * 0.3;
        if (choice > definition.density * biomeWeight * visibility) continue;

        const scaleChoice = hash2(chunkX * 757 + index, chunkZ * 761, seed + 47);
        const scale = definition.minScale
          + (definition.maxScale - definition.minScale) * scaleChoice;
        const rotation = hash2(chunkX * 769 + index, chunkZ * 773, seed + 61) * Math.PI * 2;
        const footprint = this.runtimeModelFootprint(definition, scale);
        if (obstacles.some((obstacle) => {
          const dx = obstacle.x - worldX;
          const dz = obstacle.z - worldZ;
          const clearance = obstacle.radius + footprint + 0.65;
          return dx * dx + dz * dz < clearance * clearance;
        })) continue;

        const height = terrainHeight(worldX, worldZ, weights);
        placements.push({
          x: worldX - startX,
          y: height - anchorHeight - definition.sink * scale,
          z: worldZ - startZ,
          scale,
          rotation,
        });
        this.addRuntimeCollisions(obstacles, definition, worldX, worldZ, scale, rotation);
      }

      const geometry = this.runtimeModelGeometries.get(definition.id);
      if (geometry) this.addRuntimeModelInstances(group, placements, geometry, definition.landmark ?? false);
    }
  }

  private runtimeModelFootprint(definition: RuntimeModelDefinition, scale: number): number {
    let radius = 0;
    for (const circle of definition.collisionCircles) {
      radius = Math.max(radius, Math.hypot(circle.x, circle.z) + circle.radius);
    }
    return radius * scale;
  }

  private addRuntimeCollisions(
    obstacles: Obstacle[],
    definition: RuntimeModelDefinition,
    worldX: number,
    worldZ: number,
    scale: number,
    rotation: number,
  ): void {
    const cosine = Math.cos(rotation);
    const sine = Math.sin(rotation);
    for (const circle of definition.collisionCircles) {
      const localX = circle.x * scale;
      const localZ = circle.z * scale;
      obstacles.push({
        x: worldX + localX * cosine + localZ * sine,
        z: worldZ - localX * sine + localZ * cosine,
        radius: circle.radius * scale,
        kind: definition.collisionKind,
      });
    }
  }

  private addRuntimeModelInstances(
    group: THREE.Group,
    placements: readonly RuntimeModelPlacement[],
    geometry: THREE.BufferGeometry,
    landmark: boolean,
  ): void {
    if (placements.length === 0) return;
    const mesh = new THREE.InstancedMesh(geometry, this.runtimeModelMaterial, placements.length);
    const matrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const rotation = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    const euler = new THREE.Euler();

    placements.forEach((placement, index) => {
      position.set(placement.x, placement.y, placement.z);
      euler.set(0, placement.rotation, 0);
      rotation.setFromEuler(euler);
      scale.setScalar(placement.scale);
      matrix.compose(position, rotation, scale);
      mesh.setMatrixAt(index, matrix);
    });
    mesh.name = `${geometry.name}-instances`;
    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = landmark;
    mesh.receiveShadow = true;
    group.add(mesh);
  }

  private createTerrainGeometry(startX: number, startZ: number, anchorHeight: number): THREE.BufferGeometry {
    const row = CHUNK_SEGMENTS + 1;
    const vertexCount = row * row;
    const positions = new Float32Array(vertexCount * 3);
    const colors = new Float32Array(vertexCount * 3);
    const indices: number[] = [];

    for (let zIndex = 0; zIndex <= CHUNK_SEGMENTS; zIndex += 1) {
      for (let xIndex = 0; xIndex <= CHUNK_SEGMENTS; xIndex += 1) {
        const index = zIndex * row + xIndex;
        const localX = (xIndex / CHUNK_SEGMENTS) * CHUNK_SIZE;
        const localZ = (zIndex / CHUNK_SEGMENTS) * CHUNK_SIZE;
        const worldX = startX + localX;
        const worldZ = startZ + localZ;
        const weights = biomeWeightsAt(worldX, worldZ);
        const height = terrainHeight(worldX, worldZ, weights);
        const color = terrainColor(worldX, worldZ, height, weights);

        positions[index * 3] = localX;
        positions[index * 3 + 1] = height - anchorHeight;
        positions[index * 3 + 2] = localZ;
        colors[index * 3] = color[0];
        colors[index * 3 + 1] = color[1];
        colors[index * 3 + 2] = color[2];
      }
    }

    for (let zIndex = 0; zIndex < CHUNK_SEGMENTS; zIndex += 1) {
      for (let xIndex = 0; xIndex < CHUNK_SEGMENTS; xIndex += 1) {
        const topLeft = zIndex * row + xIndex;
        const topRight = topLeft + 1;
        const bottomLeft = topLeft + row;
        const bottomRight = bottomLeft + 1;
        const flip = (xIndex + zIndex) % 2 === 0;
        if (flip) {
          indices.push(topLeft, bottomLeft, bottomRight, topLeft, bottomRight, topRight);
        } else {
          indices.push(topLeft, bottomLeft, topRight, topRight, bottomLeft, bottomRight);
        }
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    return geometry;
  }

  private addTrees(group: THREE.Group, placements: readonly TreePlacement[]): void {
    if (placements.length === 0) return;

    const trunks = new THREE.InstancedMesh(this.trunkGeometry, this.trunkMaterial, placements.length);
    const matrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const rotation = new THREE.Quaternion();
    const euler = new THREE.Euler();
    const scale = new THREE.Vector3();

    placements.forEach((tree, index) => {
      euler.set(0, tree.rotation, 0);
      rotation.setFromEuler(euler);
      position.set(tree.x, tree.y, tree.z);
      scale.set(tree.scale * 0.74, tree.scale, tree.scale * 0.74);
      matrix.compose(position, rotation, scale);
      trunks.setMatrixAt(index, matrix);
    });

    trunks.instanceMatrix.needsUpdate = true;
    // At the sun's wide 270 m shadow footprint, trunks and the fine upper
    // tiers are sub-texel detail. Keep one broad canopy caster per chunk for
    // readable grounding without resubmitting every tree layer to the shadow
    // pass on mobile GPUs.
    trunks.castShadow = false;
    group.add(trunks);

    const maxLayerCount = PINE_LAYER_COUNTS[PINE_LAYER_COUNTS.length - 1]!;
    for (let layerIndex = 0; layerIndex < maxLayerCount; layerIndex += 1) {
      const trees = placements.filter((tree) => tree.pineLayers > layerIndex);
      if (trees.length === 0) continue;

      const pines = new THREE.InstancedMesh(this.pineLayerGeometry, this.pineMaterial, trees.length);

      trees.forEach((tree, index) => {
        const layer = pineLayerShape(layerIndex, tree.pineLayers);
        euler.set(0, tree.layerRotations[layerIndex]!, 0);
        rotation.setFromEuler(euler);

        position.set(tree.x, tree.y + layer.y * tree.scale, tree.z);
        scale.set(
          tree.scale * tree.width * layer.radius,
          tree.scale,
          tree.scale * tree.width * layer.radius,
        );
        matrix.compose(position, rotation, scale);
        pines.setMatrixAt(index, matrix);
        pines.setColorAt(index, tree.tint);
      });

      pines.instanceMatrix.needsUpdate = true;
      pines.castShadow = layerIndex === 0;
      if (pines.instanceColor) pines.instanceColor.needsUpdate = true;
      group.add(pines);
    }
  }

  private addObjects(
    group: THREE.Group,
    placements: readonly ObjectPlacement[],
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    castShadow = false,
  ): void {
    if (placements.length === 0) return;

    const mesh = new THREE.InstancedMesh(geometry, material, placements.length);
    const matrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const rotation = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    const euler = new THREE.Euler();

    placements.forEach((object, index) => {
      position.set(object.x, object.y, object.z);
      euler.set(
        object.tiltX ?? Math.sin(object.rotation * 2.17) * 0.08,
        object.rotation,
        object.tiltZ ?? Math.cos(object.rotation * 1.43) * 0.1,
      );
      rotation.setFromEuler(euler);
      scale.set(object.scaleX, object.scaleY, object.scaleZ);
      matrix.compose(position, rotation, scale);
      mesh.setMatrixAt(index, matrix);
      mesh.setColorAt(index, object.tint);
    });

    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = castShadow;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    group.add(mesh);
  }

  private chunkKey(x: number, z: number): string {
    return `${x}:${z}`;
  }

  private collisionBinKey(x: number, z: number): string {
    return `${x}:${z}`;
  }

  private disposeChunk(chunk: TerrainChunk): void {
    chunk.group.traverse((object) => {
      if (object instanceof THREE.InstancedMesh) object.dispose();
    });
    chunk.terrainGeometry.dispose();
  }
}
